import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, MapPin, Phone, FileText, Shield, AlertCircle } from "lucide-react";
import { useAuthStore } from "../../state/auth";
import { useTranslation } from "../../i18n";

interface SignupFormData {
  // Personal Information
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  password: string;
  confirmPassword: string;

  // Address Information
  address: string;
  city: string;
  state: string;
  zipCode: string;

  // Role and Availability
  requestedRole: "GUARD" | "SUPERVISOR";
  availableShifts: string[];

  // Emergency Contact
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelation: string;

  // Terms
  agreeToTerms: boolean;
}

export function SignupPage() {
  const navigate = useNavigate();
  const { signup, loading } = useAuthStore();
  const { t } = useTranslation();

  const SHIFT_LABELS: Record<string, string> = {
    'Day Shift (6am-2pm)': t("landing.shiftDay", "Day Shift (6am-2pm)"),
    'Evening Shift (2pm-10pm)': t("landing.shiftEvening", "Evening Shift (2pm-10pm)"),
    'Night Shift (10pm-6am)': t("landing.shiftNight", "Night Shift (10pm-6am)"),
    'Weekends': t("landing.shiftWeekends", "Weekends"),
    'Holidays': t("landing.shiftHolidays", "Holidays"),
    'On-call': t("landing.shiftOnCall", "On-call"),
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const [formData, setFormData] = useState<SignupFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    dateOfBirth: "",
    password: "",
    confirmPassword: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    requestedRole: "GUARD",
    availableShifts: [],
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelation: "",
    agreeToTerms: false
  });

  const updateFormData = (field: keyof SignupFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Real-time duplicate checking for email and phone
    if (field === 'email') {
      setEmailError(null);
      if (value) {
        const duplicate = checkEmailDuplicate(value);
        if (duplicate) {
          setEmailError(t("landing.emailRegistered", "This email address is already registered."));
        }
      }
    }

    if (field === 'phone') {
      setPhoneError(null);
      if (value) {
        const duplicate = checkPhoneDuplicate(value);
        if (duplicate) {
          setPhoneError(t("landing.phoneRegistered", "This phone number is already registered."));
        }
      }
    }
  };

  const checkEmailDuplicate = (email: string): boolean => {
    const demoUsers = ["admin@demo.com", "admin@emory.local", "supervisor@demo.com", "guard@demo.com", "guard@emory.local"];
    const demoPendingUsers = ["michael.johnson@email.com", "amanda.rodriguez@email.com", "james.wilson@email.com"];
    const storedApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");

    const allEmails = [
      ...demoUsers,
      ...demoPendingUsers,
      ...storedApplications.map((app: any) => app.email)
    ].filter(Boolean);

    return allEmails.some(existingEmail =>
      existingEmail.toLowerCase() === email.toLowerCase()
    );
  };

  const checkPhoneDuplicate = (phone: string): boolean => {
    const demoPendingUsers = ["(555) 123-4567", "(555) 234-5678", "(555) 345-6789"];
    const storedApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");

    const allPhones = [
      ...demoPendingUsers,
      ...storedApplications.map((app: any) => app.phone)
    ].filter(Boolean);

    return allPhones.some(existingPhone => existingPhone === phone);
  };

  const toggleShift = (shift: string) => {
    setFormData(prev => ({
      ...prev,
      availableShifts: prev.availableShifts.includes(shift)
        ? prev.availableShifts.filter(s => s !== shift)
        : [...prev.availableShifts, shift]
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.firstName && formData.lastName && formData.email &&
          formData.phone && formData.dateOfBirth && formData.password &&
          formData.confirmPassword && formData.password === formData.confirmPassword &&
          !emailError && !phoneError); // Also check for duplicate errors
      case 2:
        return !!(formData.address && formData.city && formData.state && formData.zipCode);
      case 3:
        return !!(formData.requestedRole); // Only require role selection, shifts are optional
      case 4:
        return !!(formData.emergencyContactName && formData.emergencyContactPhone &&
          formData.emergencyContactRelation && formData.agreeToTerms);
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      setError(null);
    } else {
      setError(t("landing.fillRequiredContinue", "Please fill in all required fields before continuing."));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  };

  const checkForDuplicates = (): string | null => {
    // Check existing demo users
    const demoUsers = [
      { email: "admin@demo.com", phone: "" },
      { email: "admin@emory.local", phone: "" },
      { email: "supervisor@demo.com", phone: "" },
      { email: "guard@demo.com", phone: "" },
      { email: "guard@emory.local", phone: "" }
    ];

    // Check demo data from UserManagementPage
    const demoPendingUsers = [
      { email: "michael.johnson@email.com", phone: "(555) 123-4567" },
      { email: "amanda.rodriguez@email.com", phone: "(555) 234-5678" },
      { email: "james.wilson@email.com", phone: "(555) 345-6789" }
    ];

    // Check stored applications
    const storedApplications = JSON.parse(localStorage.getItem("pendingApplications") || "[]");

    // Combine all existing users
    const allExistingUsers = [
      ...demoUsers,
      ...demoPendingUsers,
      ...storedApplications
    ];

    // Check for duplicate email
    const emailExists = allExistingUsers.some(user =>
      user.email && user.email.toLowerCase() === formData.email.toLowerCase()
    );

    if (emailExists) {
      return t("landing.emailRegisteredUseDifferent", "This email address is already registered. Please use a different email address.");
    }

    // Check for duplicate phone number
    const phoneExists = allExistingUsers.some(user =>
      user.phone && user.phone === formData.phone
    );

    if (phoneExists) {
      return t("landing.phoneRegisteredUseDifferent", "This phone number is already registered. Please use a different phone number.");
    }

    return null;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validateStep(4)) {
      setError(t("landing.completeAndAgree", "Please complete all required fields and agree to the terms."));
      return;
    }

    // Check for duplicates
    const duplicateError = checkForDuplicates();
    if (duplicateError) {
      setError(duplicateError);
      return;
    }

    setError(null);

    try {
      await signup(formData);
      setSubmitSuccess(true);
    } catch (err) {
      setError(t("landing.submitFailed", "Failed to submit application. Please try again."));
    }
  };

  if (submitSuccess) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-green-200 bg-white p-6 shadow-sm">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h1 className="text-xl font-semibold text-gray-800 mb-2">{t("landing.requestSubmitted", "Account Request Submitted")}</h1>
          <p className="text-sm text-gray-600 mb-4">
            {t("landing.requestSubmittedThanks", "Thank you for requesting access to the security patrol system. Your request has been submitted and is pending approval by supervisors.")}
          </p>
          <p className="text-xs text-gray-500 mb-6">
            {t("landing.requestSubmittedApproval", "You will be able to log in with your credentials once approved. This process typically takes 1-2 business days.")}
          </p>
          <button
            onClick={() => navigate("/login")}
            className="w-full rounded-md py-2 transition-opacity hover:opacity-90 brand-green-gradient"
          >
            {t("landing.returnToLogin", "Return to Login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">{t("landing.requestAccessTitle", "Request System Access")}</h1>
        <p className="mt-1 text-sm text-gray-600">
          {t("landing.requestAccessSubtitle", "Complete this form to request access to the security patrol system.")}
        </p>

        {/* Progress Bar */}
        <div className="mt-4 mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>{t("landing.stepOf", "Step {n} of 4").replace("{n}", String(currentStep))}</span>
            <span>{t("landing.percentComplete", "{p}% Complete").replace("{p}", String(Math.round((currentStep / 4) * 100)))}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-sm h-2">
            <div
              className="brand-green-gradient h-2 rounded-sm transition-all duration-300"
              style={{ width: `${(currentStep / 4) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-medium text-gray-800">{t("landing.personalInfo", "Personal Information")}</h2>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.firstName", "First Name")} *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => updateFormData('firstName', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.lastName", "Last Name")} *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => updateFormData('lastName', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("landing.emailAddress", "Email Address")} *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
                className={`w-full rounded-md border p-2 focus:ring-1 ${emailError
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500'
                  }`}
                required
              />
              {emailError && (
                <p className="mt-1 text-sm text-red-600">{emailError}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("auth.password", "Password")} *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  placeholder={t("landing.createPassword", "Create a secure password")}
                  minLength={8}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.confirmPassword", "Confirm Password")} *
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  placeholder={t("landing.confirmYourPassword", "Confirm your password")}
                  required
                />
              </div>
            </div>

            {formData.password && formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <div className="text-sm text-red-600">
                {t("landing.passwordsNoMatch", "Passwords do not match")}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.phoneNumber", "Phone Number")} *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateFormData('phone', e.target.value)}
                  className={`w-full rounded-md border p-2 focus:ring-1 ${phoneError
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-emerald-500 focus:ring-emerald-500'
                    }`}
                  placeholder="(555) 123-4567"
                  required
                />
                {phoneError && (
                  <p className="mt-1 text-sm text-red-600">{phoneError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.dateOfBirth", "Date of Birth")} *
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => updateFormData('dateOfBirth', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Address Information */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-medium text-gray-800">{t("landing.addressInfo", "Address Information")}</h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("landing.streetAddress", "Street Address")} *
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateFormData('address', e.target.value)}
                className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.city", "City")} *
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateFormData('city', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.state", "State")} *
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => updateFormData('state', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  placeholder="GA"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.zipCode", "ZIP Code")} *
                </label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => updateFormData('zipCode', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Role and Availability */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-medium text-gray-800">{t("landing.roleAvailability", "Role and Availability")}</h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("landing.requestedRole", "Requested Role")} *
              </label>
              <select
                value={formData.requestedRole}
                onChange={(e) => updateFormData('requestedRole', e.target.value as "GUARD" | "SUPERVISOR" | "DIRECTOR")}
                className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                required
              >
                <option value="GUARD">{t("role.guard", "Security Guard")}</option>
                <option value="SUPERVISOR">{t("landing.securitySupervisor", "Security Supervisor")}</option>
                <option value="DIRECTOR">{t("landing.securityDirector", "Security Director")}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("landing.availableShifts", "Available Shifts (select all that apply)")}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['Day Shift (6am-2pm)', 'Evening Shift (2pm-10pm)', 'Night Shift (10pm-6am)', 'Weekends', 'Holidays', 'On-call'].map((shift) => (
                  <label key={shift} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.availableShifts.includes(shift)}
                      onChange={() => toggleShift(shift)}
                      className="rounded border-gray-300 focus:ring-emerald-500"
                    />
                    <span className="text-sm text-gray-700">{SHIFT_LABELS[shift] ?? shift}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Emergency Contact & Terms */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <Phone className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-medium text-gray-800">{t("landing.emergencyContact", "Emergency Contact")}</h2>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("landing.emergencyContactName", "Emergency Contact Name")} *
              </label>
              <input
                type="text"
                value={formData.emergencyContactName}
                onChange={(e) => updateFormData('emergencyContactName', e.target.value)}
                className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.emergencyContactPhone", "Emergency Contact Phone")} *
                </label>
                <input
                  type="tel"
                  value={formData.emergencyContactPhone}
                  onChange={(e) => updateFormData('emergencyContactPhone', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("landing.relationship", "Relationship")} *
                </label>
                <select
                  value={formData.emergencyContactRelation}
                  onChange={(e) => updateFormData('emergencyContactRelation', e.target.value)}
                  className="w-full rounded-md border border-gray-300 p-2 focus:border-amber-500 focus:ring-1 focus:ring-emerald-500"
                  required
                >
                  <option value="">{t("landing.selectEllipsis", "Select...")}</option>
                  <option value="Spouse">{t("landing.relSpouse", "Spouse")}</option>
                  <option value="Parent">{t("landing.relParent", "Parent")}</option>
                  <option value="Sibling">{t("landing.relSibling", "Sibling")}</option>
                  <option value="Child">{t("landing.relChild", "Child")}</option>
                  <option value="Friend">{t("landing.relFriend", "Friend")}</option>
                  <option value="Other">{t("landing.relOther", "Other")}</option>
                </select>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-5 h-5 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-800">{t("landing.termsOfService", "Terms of Service")}</h3>
              </div>

              <div>
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.agreeToTerms}
                    onChange={(e) => updateFormData('agreeToTerms', e.target.checked)}
                    className="rounded border-gray-300 focus:ring-emerald-500 mt-1"
                    required
                  />
                  <div className="text-sm text-gray-700">
                    <span className="font-medium">{t("landing.agreeTerms", "I agree to the terms of service")} *</span>
                    <p className="mt-1">
                      {t("landing.agreeTermsDetail", "I understand that access to the security patrol system is contingent upon supervisor approval and that all information provided is accurate and complete.")}
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("common.back", "Back")}
          </button>

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-6 py-2 rounded-md transition-opacity hover:opacity-90 brand-green-gradient"
            >
              {t("landing.next", "Next")}
            </button>
          ) : (
            <button
              type="submit"
              disabled={loading || !validateStep(4)}
              className="px-6 py-2 rounded-md transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed brand-green-gradient"
            >
              {loading ? t("landing.submitting", "Submitting...") : t("landing.submitApplication", "Submit Application")}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
